import { useEffect, useState } from "react";
import {
  Box, VStack, HStack, Text, Center, IconButton, Image, Icon,
} from "@chakra-ui/react";
import { FiRotateCw, FiDownload, FiChevronLeft, FiChevronRight, FiImage, FiZoomIn, FiZoomOut } from "react-icons/fi";
import { downloadImageAsFile } from "Utils/s3Utils";

const BRAND = "#C41E3A";
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.5;

export function CoolerImagePanel({ images, imageIndex, setImageIndex, imageRotation, setImageRotation, storeName }) {
  const [zoom, setZoom] = useState(1);

  // Reset zoom whenever the visible image changes (prev/next, or a new store's images loaded in)
  useEffect(() => {
    setZoom(1);
  }, [imageIndex, images]);

  if (!images || images.length === 0) {
    return (
      <Center
        minH="460px" bg="gray.50" borderRadius="xl"
        border="2px dashed" borderColor="gray.200" flexDir="column" gap={4}
      >
        <Box p={4} borderRadius="full" bg="gray.100">
          <Icon as={FiImage} boxSize={8} color="gray.300" />
        </Box>
        <VStack spacing={1}>
          <Text color="gray.500" fontWeight="semibold" fontSize="sm">
            {storeName ? "No cooler images available" : "Select a store"}
          </Text>
          <Text color="gray.400" fontSize="xs" textAlign="center" px={6}>
            {storeName
              ? "Cooler images for this outlet will appear here"
              : "Choose an outlet to load visicooler images"}
          </Text>
        </VStack>
      </Center>
    );
  }

  const handleDownload = () => {
    downloadImageAsFile(images[imageIndex], `cooler-${imageIndex + 1}.jpg`);
  };

  return (
    <VStack spacing={3}>
      <Box
        position="relative" w="full" borderRadius="xl"
        overflow="hidden" boxShadow="lg" bg="gray.900" h="460px"
      >
        <Box position="absolute" inset={0} overflowX="hidden" overflowY="auto">
          <Box
            style={{
              width: "100%",
              height: `${(100 * zoom).toFixed(0)}%`,
              minHeight: "100%",
              margin: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Image
              src={images[imageIndex]}
              alt={`Cooler image ${imageIndex + 1}`}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                transform: `rotate(${imageRotation}deg)`,
                transition: "transform 0.3s ease",
              }}
            />
          </Box>
        </Box>
        <HStack position="absolute" top={2} right={2} spacing={1}>
          <IconButton
            aria-label="Zoom out" icon={<FiZoomOut />} size="sm" colorScheme="gray"
            isDisabled={zoom <= MIN_ZOOM}
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, +(z - ZOOM_STEP).toFixed(2)))}
          />
          <IconButton
            aria-label="Zoom in" icon={<FiZoomIn />} size="sm" colorScheme="gray"
            isDisabled={zoom >= MAX_ZOOM}
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, +(z + ZOOM_STEP).toFixed(2)))}
          />
          <IconButton
            aria-label="Rotate" icon={<FiRotateCw />} size="sm" colorScheme="blue"
            onClick={() => setImageRotation((r) => (r + 90) % 360)}
          />
          <IconButton
            aria-label="Download" icon={<FiDownload />} size="sm" colorScheme="red"
            onClick={handleDownload}
          />
        </HStack>
        {images.length > 1 && (
          <>
            <IconButton
              aria-label="Previous" icon={<FiChevronLeft />} size="sm"
              position="absolute" left={2} top="50%" transform="translateY(-50%)"
              bg="whiteAlpha.900" boxShadow="md" isDisabled={imageIndex === 0}
              onClick={() => { setImageIndex((i) => i - 1); setImageRotation(0); }}
            />
            <IconButton
              aria-label="Next" icon={<FiChevronRight />} size="sm"
              position="absolute" right={2} top="50%" transform="translateY(-50%)"
              bg="whiteAlpha.900" boxShadow="md" isDisabled={imageIndex === images.length - 1}
              onClick={() => { setImageIndex((i) => i + 1); setImageRotation(0); }}
            />
          </>
        )}
      </Box>

      <Text fontSize="xs" color="gray.500" fontWeight="semibold">
        {images.length > 1 ? `${imageIndex + 1} / ${images.length} · ` : ""}Zoom {Math.round(zoom * 100)}%
      </Text>

      {images.length > 1 && (
        <>
          <HStack spacing={2} overflowX="auto" py={1} justify="center">
            {images.map((img, idx) => (
              <Box
                key={idx} cursor="pointer"
                opacity={idx === imageIndex ? 1 : 0.45}
                border="2px solid" borderColor={idx === imageIndex ? BRAND : "transparent"}
                borderRadius="md" overflow="hidden" transition="all 0.2s"
                _hover={{ opacity: 0.8 }}
                onClick={() => { setImageIndex(idx); setImageRotation(0); }}
              >
                <Image src={img} alt={`thumb ${idx}`} w="56px" h="56px" objectFit="cover" />
              </Box>
            ))}
          </HStack>
        </>
      )}
    </VStack>
  );
}